import React, { useState, useEffect } from 'react';
import { 
    List, 
    useListContext,
    useDataProvider,
    TopToolbar,
    Edit,
    SimpleForm,
    useRecordContext,
    useNotify,
    useRedirect,
    ListButton
} from 'react-admin';
import { 
    Box, 
    Grid, 
    Typography, 
    Paper, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Button,
    Chip,
    Autocomplete,
    TextField as MuiTextField,
    CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PetsRoundedIcon from '@mui/icons-material/PetsRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import DetailLoadingState from './components/DetailLoadingState';

let animalsNamesCache = null;

// Компонент для отображения списка популярных животных
const PopularAnimalsDatagrid = () => {
    const { data, isPending } = useListContext();
    const [animalsMap, setAnimalsMap] = useState(() => animalsNamesCache || {});
    const [isResolvingAnimals, setIsResolvingAnimals] = useState(() => !animalsNamesCache);
    const dataProvider = useDataProvider();

    // Загружаем всех животных для сопоставления ID с названиями
    useEffect(() => {
        if (animalsNamesCache) {
            setAnimalsMap(animalsNamesCache);
            setIsResolvingAnimals(false);
            return;
        }

        setIsResolvingAnimals(true);
        dataProvider.getList('animals', {
            pagination: { page: 1, perPage: 1000 },
            sort: { field: 'id', order: 'ASC' },
            filter: {},
        }).then(({ data }) => {
            const map = {};
            data.forEach(animal => {
                map[animal.id] = animal.name;
            });
            animalsNamesCache = map;
            setAnimalsMap(map);
        }).catch(error => {
            console.error('Error loading animals:', error);
        }).finally(() => {
            setIsResolvingAnimals(false);
        });
    }, [dataProvider]);

    const hasPopularRecords = Array.isArray(data)
        && data.some((record) => Array.isArray(record?.animals_id) && record.animals_id.length > 0);

    if (isPending || (hasPopularRecords && isResolvingAnimals)) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.5,
                    py: 4,
                    color: 'text.secondary',
                }}
            >
                <CircularProgress size={22} />
                <Typography variant="body2">
                    Подготавливаем список популярных животных...
                </Typography>
            </Box>
        );
    }
    
    return (
        <Box sx={{ display: 'grid', gap: 2 }}>
            {(data || []).map((record) => {
                const animalIds = Array.isArray(record.animals_id) ? record.animals_id : [];
                return (
                    <Paper
                        key={record.id}
                        component="a"
                        href={`#/popular-animals/${record.id}`}
                        elevation={0}
                        sx={{
                            p: { xs: 2, md: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 3,
                            color: 'inherit', textDecoration: 'none', transition: '160ms ease',
                            '&:hover': { borderColor: 'primary.main', transform: 'translateY(-2px)', boxShadow: '0 12px 30px rgba(15, 23, 42, .08)' },
                            '&:focus-visible': { outline: '3px solid', outlineColor: 'primary.light', outlineOffset: 2 },
                        }}
                    >
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', justifyContent: 'space-between', mb: 2.5 }}>
                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                <Box sx={{ width: 46, height: 46, borderRadius: 2.5, display: 'grid', placeItems: 'center', bgcolor: 'rgba(249, 115, 22, .12)', color: 'warning.main' }}>
                                    <StarRoundedIcon />
                                </Box>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 800 }}>Подборка для главного экрана</Typography>
                                    <Typography variant="body2" color="text.secondary">Список #{record.id} · {animalIds.length} животных</Typography>
                                </Box>
                            </Box>
                            <Chip label="Активна" size="small" color="success" variant="outlined" />
                        </Box>
                        {animalIds.length ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {animalIds.map((animalId, index) => (
                                    <Chip key={`${animalId}-${index}`} icon={<PetsRoundedIcon />} label={`${index + 1}. ${animalsMap[animalId] || `Животное #${animalId}`}`} />
                                ))}
                            </Box>
                        ) : (
                            <Typography color="text.secondary">Подборка пока пустая — откройте её и добавьте животных.</Typography>
                        )}
                    </Paper>
                );
            })}
        </Box>
    );
};

// Кастомные actions для списка с кнопкой создания
const PopularAnimalsListActions = () => {
    const { data } = useListContext();
    const [createDialogOpen, setCreateDialogOpen] = useState(false);

    // Показываем кнопку создания только если список пуст
    const hasRecords = data && data.length > 0;

    return (
        <>
            <TopToolbar>
                {!hasRecords && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateDialogOpen(true)}
                        sx={{ fontWeight: 600 }}
                    >
                        Создать список
                    </Button>
                )}
            </TopToolbar>
            <CreatePopularAnimalsDialog 
                open={createDialogOpen} 
                onClose={() => setCreateDialogOpen(false)} 
            />
        </>
    );
};

// Диалог создания списка популярных животных
const CreatePopularAnimalsDialog = ({ open, onClose }) => {
    const [selectedAnimals, setSelectedAnimals] = useState([]);
    const [animals, setAnimals] = useState([]);
    
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const redirect = useRedirect();

    // Загружаем всех животных при открытии диалога
    useEffect(() => {
        if (open) {
            dataProvider.getList('animals', {
                pagination: { page: 1, perPage: 1000 },
                sort: { field: 'name', order: 'ASC' },
                filter: {}
            }).then(({ data }) => {
                setAnimals(data);
            }).catch(error => {
                console.error('Error loading animals:', error);
                notify('Ошибка загрузки животных', { type: 'error' });
            });
        }
    }, [open, dataProvider, notify]);

    const handleCreate = async () => {
        if (selectedAnimals.length === 0) {
            notify('Выберите хотя бы одно животное', { type: 'warning' });
            return;
        }

        try {
            const result = await dataProvider.create('popular-animals', {
                data: {
                    animals_id: selectedAnimals.map(a => a.id)
                }
            });
            
            notify('Список популярных животных успешно создан', { type: 'success' });
            handleClose();
            redirect('edit', 'popular-animals', result.data.id);
        } catch (error) {
            console.error('Error creating popular animals:', error);
            notify(`Ошибка при создании: ${error.message}`, { type: 'error' });
        }
    };

    const handleClose = () => {
        setSelectedAnimals([]);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ fontWeight: 800 }}>Создать подборку</DialogTitle>
            <DialogContent>
                <Typography color="text.secondary" sx={{ mb: 2 }}>Выберите животных, которые посетители увидят на главном экране приложения.</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <Autocomplete
                        multiple
                        options={animals}
                        getOptionLabel={(option) => `${option.id} - ${option.name}`}
                        value={selectedAnimals}
                        onChange={(event, newValue) => {
                            setSelectedAnimals(newValue);
                        }}
                        renderInput={(params) => (
                            <MuiTextField
                                {...params}
                                label="Выберите животных *"
                                placeholder="Начните вводить название или ID"
                            />
                        )}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    label={`${option.id} - ${option.name}`}
                                    {...getTagProps({ index })}
                                    key={option.id}
                                />
                            ))
                        }
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Отмена</Button>
                <Button 
                    onClick={handleCreate} 
                    variant="contained" 
                    disabled={selectedAnimals.length === 0}
                >
                    Создать
                </Button>
            </DialogActions>
        </Dialog>
    );
};


// Пустой компонент для отображения когда нет данных
const EmptyPopularAnimalsList = () => {
    const [createDialogOpen, setCreateDialogOpen] = useState(false);

    return (
        <>
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                minHeight: 'calc(100vh - 200px)',
                gap: 2
            }}>
                <Typography variant="h6" color="text.secondary">
                    Нет списков популярных животных
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setCreateDialogOpen(true)}
                >
                    Создать список
                </Button>
            </Box>
            <CreatePopularAnimalsDialog 
                open={createDialogOpen} 
                onClose={() => setCreateDialogOpen(false)} 
            />
        </>
    );
};

// Компонент списка популярных животных
export const PopularAnimalsList = (props) => (
    <List 
        {...props}
        exporter={false}
        title="Популярные животные"
        perPage={30}
        sort={{ field: 'id', order: 'ASC' }}
        actions={<PopularAnimalsListActions />}
        empty={<EmptyPopularAnimalsList />}
        sx={{ width: '100%', '& .RaList-actions': { mb: 1 }, '& .RaList-content': { width: '100%', boxShadow: 'none', bgcolor: 'transparent' } }}
    >
        <Box sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 850, letterSpacing: '-.03em', mb: .75 }}>Популярные животные</Typography>
            <Typography color="text.secondary">Управляйте подборкой, которая встречает посетителя на главном экране приложения.</Typography>
        </Box>
        <PopularAnimalsDatagrid />
    </List>
);

// Кастомные actions для страницы редактирования
const PopularAnimalsEditActions = () => (
    <TopToolbar>
        <ListButton label="К списку" icon={<ArrowBackIcon />} />
    </TopToolbar>
);

// Контент формы редактирования
const PopularAnimalsEditContent = () => {
    const record = useRecordContext();
    const [animals, setAnimals] = useState([]);
    const [selectedAnimals, setSelectedAnimals] = useState([]);
    const [animalsLoading, setAnimalsLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const redirect = useRedirect();

    // Загружаем всех животных
    useEffect(() => {
        setAnimalsLoading(true);
        dataProvider.getList('animals', {
            pagination: { page: 1, perPage: 1000 },
            sort: { field: 'name', order: 'ASC' },
            filter: {}
        }).then(({ data }) => {
            setAnimals(data);
            
            // Создаем карту ID -> животное
            const map = {};
            data.forEach(animal => {
                map[animal.id] = animal;
            });

            // Устанавливаем выбранных животных на основе record
            if (record && record.animals_id) {
                const selected = record.animals_id
                    .map(id => map[id])
                    .filter(animal => animal !== undefined);
                setSelectedAnimals(selected);
            }
        }).catch(error => {
            console.error('Error loading animals:', error);
            notify('Ошибка загрузки животных', { type: 'error' });
        }).finally(() => {
            setAnimalsLoading(false);
        });
    }, [dataProvider, notify, record]);

    const handleSave = async () => {
        try {
            await dataProvider.update('popular-animals', {
                id: record.id,
                data: {
                    animals_id: selectedAnimals.map(a => a.id)
                },
                previousData: record
            });
            
            notify('Список популярных животных успешно обновлен', { type: 'success' });
            redirect('list', 'popular-animals');
        } catch (error) {
            console.error('Error updating popular animals:', error);
            notify(`Ошибка при сохранении: ${error.message}`, { type: 'error' });
        }
    };

    const moveAnimal = (index, direction) => {
        const nextIndex = index + direction;
        if (nextIndex < 0 || nextIndex >= selectedAnimals.length) return;
        const next = [...selectedAnimals];
        [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
        setSelectedAnimals(next);
    };

    const removeAnimal = (index) => setSelectedAnimals((current) => current.filter((_, itemIndex) => itemIndex !== index));
    
    if (!record || animalsLoading) {
        return <DetailLoadingState title="Загружаем список популярных животных..." />;
    }
    
    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ mb: 3 }}>
                <Typography variant="overline" color="primary" sx={{ fontWeight: 800 }}>Главный экран приложения</Typography>
                <Typography variant="h4" sx={{ fontWeight: 850, letterSpacing: '-.03em' }}>Популярные животные</Typography>
                <Typography color="text.secondary" sx={{ mt: .75 }}>Добавляйте животных и задавайте порядок карточек слева направо.</Typography>
            </Box>
            <Grid container spacing={3}>
                <Grid item xs={12} lg={8}>
                    <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, mb: .5 }}>Состав подборки</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>Найдите животное по имени или ID. Уже выбранные позиции не дублируются.</Typography>
                        <Autocomplete
                            multiple options={animals} getOptionLabel={(option) => `${option.name} · #${option.id}`}
                            value={selectedAnimals} onChange={(event, newValue) => setSelectedAnimals(newValue)}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderTags={() => null}
                            renderInput={(params) => <MuiTextField {...params} label="Добавить животное" placeholder="Начните вводить название" />}
                        />
                        <Box sx={{ display: 'grid', gap: 1.25, mt: 3 }}>
                            {selectedAnimals.map((animal, index) => (
                                <Box key={animal.id} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2.5, bgcolor: 'background.default' }}>
                                    <Box sx={{ width: 34, height: 34, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 800 }}>{index + 1}</Box>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography sx={{ fontWeight: 750 }} noWrap>{animal.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">ID {animal.id}</Typography>
                                    </Box>
                                    <Button aria-label={`Переместить ${animal.name} выше`} disabled={index === 0} onClick={() => moveAnimal(index, -1)} sx={{ minWidth: 40, p: 1 }}><ArrowUpwardIcon fontSize="small" /></Button>
                                    <Button aria-label={`Переместить ${animal.name} ниже`} disabled={index === selectedAnimals.length - 1} onClick={() => moveAnimal(index, 1)} sx={{ minWidth: 40, p: 1 }}><ArrowDownwardIcon fontSize="small" /></Button>
                                    <Button color="error" aria-label={`Удалить ${animal.name}`} onClick={() => removeAnimal(index)} sx={{ minWidth: 40, p: 1 }}><DeleteOutlineIcon fontSize="small" /></Button>
                                </Box>
                            ))}
                            {!selectedAnimals.length && <Box sx={{ py: 5, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 2.5 }}><PetsRoundedIcon color="disabled" /><Typography color="text.secondary">В подборке пока никого нет</Typography></Box>}
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} lg={4}>
                    <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 3, position: { lg: 'sticky' }, top: { lg: 88 } }}>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>Публикация</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2.5, my: 2, borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Typography color="text.secondary">Выбрано</Typography><Chip label={`${selectedAnimals.length} животных`} color="primary" />
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>После сохранения новый порядок сразу станет доступен приложению.</Typography>
                        <Button fullWidth size="large" variant="contained" disabled={updating} onClick={async () => { setUpdating(true); await handleSave(); setUpdating(false); }}>
                            {updating ? 'Сохраняем…' : 'Сохранить подборку'}
                        </Button>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

// Компонент редактирования списка популярных животных
export const PopularAnimalsEdit = () => {
    return (
        <Edit title="Редактирование списка популярных животных" actions={<PopularAnimalsEditActions />}>
            <SimpleForm
                toolbar={false}
                sx={{
                    maxWidth: 'none',
                    width: '100%',
                    '& .RaSimpleForm-form': {
                        maxWidth: 'none',
                        width: '100%',
                    },
                    '& .RaSimpleForm-stack': {
                        maxWidth: 'none',
                        width: '100%',
                    },
                }}
            >
                <PopularAnimalsEditContent />
            </SimpleForm>
        </Edit>
    );
};
